/**
 * Lead Adapter - Transforma leads de la API al formato del frontend
 *
 * CONSOLIDADO: Usa tipos oficiales de:
 * - API: src/types/api/lead.api.types.ts (snake_case backend)
 * - Frontend: src/modules/radar/types (radar Lead type - legacy compatibility)
 *
 * UPDATED: 2025-10-23 - Fixed statusToStage mapping for radar visualization
 */

import { APILead } from '@/types/api/lead.api.types';
import { Lead as OfficialLead, LeadStatus, LeadSource, LeadPriority, DocumentType } from '@/types/lead';
import { Lead } from '@/modules/radar/types'; // Radar Lead type (legacy)

// Debug counter for logging
declare global {
  interface Window {
    __DEBUG_LEADS_COUNT?: number;
  }
}

/**
 * Convierte un lead de la API al formato del frontend
 * @param apiLead - Lead en formato snake_case del backend
 * @returns Lead en formato camelCase para componentes
 */
export function adaptAPILeadToFrontend(apiLead: APILead): Lead {
  // Debug: Log first lead to verify data structure
  if (window.__DEBUG_LEADS_COUNT === undefined) {
    window.__DEBUG_LEADS_COUNT = 0;
  }
  if (window.__DEBUG_LEADS_COUNT === 0) {
    window.__DEBUG_LEADS_COUNT = 1;
  }
  // Mapeo de status de API a enums del frontend
  const statusMap: Record<string, LeadStatus> = {
    'new': LeadStatus.NEW,
    'contacted': LeadStatus.CONTACTED,
    'qualified': LeadStatus.QUALIFIED,
    'proposal': LeadStatus.PROPOSAL,
    'negotiation': LeadStatus.NEGOTIATION,
    'closed_won': LeadStatus.CLOSED_WON,
    'closed_lost': LeadStatus.CLOSED_LOST,
    'nurturing': LeadStatus.NURTURING,
  };

  const priorityMap: Record<string, LeadPriority> = {
    'low': LeadPriority.LOW,
    'medium': LeadPriority.MEDIUM,
    'high': LeadPriority.HIGH,
    'urgent': LeadPriority.URGENT,
  };

  const sourceMap: Record<string, LeadSource> = {
    'website': LeadSource.WEBSITE,
    'landing_page': LeadSource.WEBSITE, // Landing pages → Web channel
    'facebook': LeadSource.FACEBOOK,
    'instagram': LeadSource.INSTAGRAM,
    'whatsapp': LeadSource.WHATSAPP,
    'referral': LeadSource.REFERRAL,
    'cold_call': LeadSource.COLD_CALL,
    'trade_show': LeadSource.TRADE_SHOW,
    'partner': LeadSource.PARTNER,
    'social_media': LeadSource.INSTAGRAM, // Map generic social_media
    'orbit_view': LeadSource.ORBIT_VIEW, // Maqueta Virtual pública
    'sala_ventas': 'sala_ventas' as LeadSource,
    'reunion_virtual': 'reunion_virtual' as LeadSource,
    'llamada': 'llamada' as LeadSource,
    'feria': 'feria' as LeadSource,
    'portal': 'portal' as LeadSource,
    'quote': 'quote' as LeadSource, // Legacy
    'email_campaign': LeadSource.OTHER,
    'phone_call': LeadSource.COLD_CALL,
    'event': LeadSource.TRADE_SHOW,
    'other': LeadSource.OTHER,
  };

  // Mapeo de status a stage del radar (para compatibilidad con radar module)
  // Stage: 0 = Qualified (centro), 1 = In Progress (medio), 2 = Cold/Lost (exterior)
  const statusToStageMap: Record<string, number> = {
    'qualified': 0,
    'proposal': 0,
    'negotiation': 0,
    'closed_won': 0,
    'new': 1,
    'contacted': 1,
    'closed_lost': 2,
    'nurturing': 2,
  };

  // Calculate radar-specific fields
  const stage = statusToStageMap[apiLead.status] ?? 1;

  // Use full_name if available, otherwise build from first_name and last_name
  const name = apiLead.full_name || `${apiLead.first_name || ''} ${apiLead.last_name || ''}`.trim() || 'Sin nombre';

  // Debug log if name is missing
  if (!apiLead.first_name && !apiLead.last_name && !apiLead.full_name) {
  }

  const value = apiLead.total_quote_value || (apiLead.budget_max || apiLead.budget_min || 0);
  const score = apiLead.lead_score || 0;

  return {
    // ====================================
    // RADAR MODULE FIELDS (Legacy - Required for visualization)
    // ====================================
    id: apiLead.id,
    name, // Full name for radar display
    avatar: apiLead.avatar_url || undefined,
    stage, // Pipeline stage (0=qualified, 1=in-progress, 2=cold)
    progress: score, // Use lead score as progress indicator
    value, // Deal value for sizing
    phone: apiLead.phone,
    email: apiLead.email,
    alternate_email: apiLead.alternate_email || '',
    unidadInteres: apiLead.preferences?.unidadInteres || (apiLead as any).interested_units_data?.[0]?.display || '', // First interested unit display
    lastContact: apiLead.last_contact_date,
    createdAt: apiLead.created_at,

    // Pipeline stage from backend (new system) - map to camelCase
    currentStage: (apiLead as any).current_stage,

    // Business status (estado de negocio: activo, cancelado, reactivado, cliente)
    business_status: apiLead.business_status,
    // Contact effort status (computed by backend: unassigned, pending_contact, etc.)
    contact_effort_status: (apiLead as any).contact_effort_status,
    next_follow_up_date: apiLead.next_follow_up_date ?? null,
    cancellation_category: (apiLead as any).cancellation_category,
    abandonment_reason: (apiLead as any).abandonment_reason,
    abandonment_notes: (apiLead as any).abandonment_notes,
    abandoned_at: (apiLead as any).abandoned_at,
    reactivated_at: (apiLead as any).reactivated_at,
    reactivated_by_quote_number: (apiLead as any).reactivated_by_quote_number,
    cancellation_reasons: (apiLead as any).cancellation_reasons,

    // Project info for filtering
    projectId: (apiLead as any).project,
    projectDetail: (apiLead as any).project_detail,

    // Asesor asignado (backend devuelve assigned_to_detail con los datos del usuario)
    assigned_to_detail: apiLead.assigned_to_detail ? {
      id: apiLead.assigned_to_detail.id,
      full_name: apiLead.assigned_to_detail.full_name || `${apiLead.assigned_to_detail.first_name || ''} ${apiLead.assigned_to_detail.last_name || ''}`.trim(),
      email: apiLead.assigned_to_detail.email || '',
      phone: apiLead.assigned_to_detail.phone,
      role: apiLead.assigned_to_detail.role,
    } : undefined,

    // Scoring object (radar legacy format)
    scoring: {
      presupuesto: score,
      zona: score,
      datosCompletos: score,
      timing: score,
      credito: score,
      total: score,
    },

    // Perfil object (radar legacy format)
    // Merge: use preferences.perfil (saved data) over flat API fields (defaults)
    perfil: {
      profesion: apiLead.preferences?.perfil?.profesion || apiLead.position || '',
      ingresos: apiLead.preferences?.perfil?.ingresos || apiLead.budget_max || 0,
      miembros: apiLead.preferences?.perfil?.miembros || 1,
      telefono: apiLead.preferences?.perfil?.telefono || apiLead.phone,
      email: apiLead.preferences?.perfil?.email || apiLead.email,
      canal: sourceMap[apiLead.source] || apiLead.preferences?.perfil?.canal || LeadSource.OTHER,
      fechaContacto: apiLead.preferences?.perfil?.fechaContacto || apiLead.last_contact_date || apiLead.created_at,
      motivacion: apiLead.preferences?.perfil?.motivacion || '',
    },

    // Preferencias de vivienda (from preferences JSON)
    preferencias: apiLead.preferences?.preferencias || undefined,

    // ====================================
    // OFFICIAL LEAD FIELDS (New consolidated structure)
    // ====================================
    // Adding key fields for dashboard components compatibility
    leadNumber: apiLead.lead_number,
    firstName: apiLead.first_name,
    lastName: apiLead.last_name,
    fullName: apiLead.full_name,
    lastContactDate: apiLead.last_contact_date,
    // alternatePhone: apiLead.alternate_phone,
    company: apiLead.company_name,
    // position: apiLead.position,
    // documentType: apiLead.document_type as DocumentType | undefined,
    // documentNumber: apiLead.document_number,
    status: apiLead.status,
    // priority: priorityMap[apiLead.priority] || LeadPriority.MEDIUM,
    sourceDetail: apiLead.source_detail,
    // leadScore: apiLead.lead_score || 0,
    // ai_close_probability: apiLead.ai_close_probability,
    data_quality_score: apiLead.data_quality_score,
    profile_score: apiLead.profile_score,
    engagement_score: apiLead.engagement_score,
    priority_matrix_label: apiLead.priority_matrix_label,
    ai_insights: apiLead.ai_insights,
    // conversion_probability_factors: apiLead.conversion_factors,
    // assignedTo: apiLead.assigned_to?.id,
    // assignedDate: apiLead.assigned_date,
    // lastContactDate: apiLead.last_contact_date,
    // lastInteractionDate: apiLead.last_interaction_date,
    // interactionCount: apiLead.interaction_count,
    // nextFollowUpDate: apiLead.next_follow_up_date,
    // interestedProjects: apiLead.interested_projects,
    // interestedUnits: apiLead.interested_units?.map(u => u.id),
    // budget: (apiLead.budget_range_min || apiLead.budget_range_max) ? {
    //   min: apiLead.budget_range_min || 0,
    //   max: apiLead.budget_range_max || 0,
    //   currency: 'COP',
    //   financing: apiLead.financing || false,
    //   downPaymentPercentage: apiLead.down_payment_percentage,
    // } : undefined,
    // preferences: apiLead.preferences ? {
    //   propertyTypes: apiLead.preferences.propertyType ? [apiLead.preferences.propertyType] : [],
    //   locations: [],
    //   minBedrooms: undefined,
    //   minBathrooms: undefined,
    //   minArea: undefined,
    //   amenities: [],
    //   moveInDate: undefined,
    // } : undefined,
    // quoteCount: apiLead.quote_count,
    // totalQuoteValue: apiLead.total_quote_value,
    // lastQuoteDate: apiLead.last_quote_date,
    notes: apiLead.notes,

    // Lead Source Taxonomy (Fase 2)
    source: apiLead.source,
    lead_source: apiLead.lead_source,
    lead_source_name: apiLead.lead_source_name,
    lead_source_category: apiLead.lead_source_category,
    lead_source_detail: apiLead.lead_source_detail,
    utm_source: apiLead.utm_source,
    utm_medium: apiLead.utm_medium,
    utm_campaign: apiLead.utm_campaign,

    // Contact Method + Attribution (Fase 3)
    contact_method: (apiLead as any).contact_method,
    contact_method_detail: (apiLead as any).contact_method_detail,
    self_reported_source: (apiLead as any).self_reported_source,
    fbclid: (apiLead as any).fbclid,
    gclid: (apiLead as any).gclid,
    origin_channel: (apiLead as any).origin_channel,

    // Tags del nuevo sistema (mapeados del backend)
    tags: apiLead.tags || [],

    // Test-mode flag from Lead.is_test column (BooleanField, db_index).
    is_test: Boolean((apiLead as any).is_test),

    // Document fields (for conversion to client)
    document_type: apiLead.document_type,
    document_number: apiLead.document_number,
    gender: apiLead.gender,
    country: apiLead.country,
    state: apiLead.state,
    city: apiLead.city,
    client_portal_active: apiLead.client_portal_active,
    has_active_conversation: apiLead.has_active_conversation,

    // Demographic fields
    date_of_birth: apiLead.date_of_birth,
    marital_status: apiLead.marital_status,
    education_level: apiLead.education_level,
    occupation: apiLead.occupation,
    housing_status: apiLead.housing_status,
    neighborhood: apiLead.neighborhood,
    address: apiLead.address,
    has_pets: apiLead.has_pets,
    pet_type: apiLead.pet_type,
    person_type: apiLead.person_type,

    // Financial fields (flat)
    budget_min: apiLead.budget_min,
    budget_max: apiLead.budget_max,
    monthly_income: apiLead.monthly_income,
    monthly_debt: apiLead.monthly_debt,
    financing: apiLead.financing,
    down_payment_percentage: apiLead.down_payment_percentage,
    employment_type: apiLead.employment_type,

    // Quotes data for multi-quote selector
    quotes_data: (apiLead as any).quotes_data,
    quote_count: apiLead.quote_count,
    total_quote_value: apiLead.total_quote_value,
    last_quote_date: apiLead.last_quote_date,

    // Submissions count for tracking captures
    submissions_count: (apiLead as any).submissions_count,

    // Deals summary (active deals count + total value)
    deals_summary: (apiLead as any).deals_summary,

    // Financial fields
    savingsAmount: apiLead.savings_amount,
    severanceAmount: apiLead.severance_amount,

    // Value from preferences (if saved there)
    ...(apiLead.preferences?.value !== undefined && { value: apiLead.preferences.value }),

    conversion_date: apiLead.conversion_date,
    updatedAt: apiLead.updated_at,

    // Broker attribution
    broker_name: (apiLead as any).broker_name,
    broker_registration: (apiLead as any).broker_registration,
    // convertedToCustomer: apiLead.converted_to_customer,
    // consentGiven: apiLead.consent_given,
    // consentDate: apiLead.consent_date,
    // marketingOptIn: apiLead.marketing_opt_in,
  } as Lead; // Cast to radar Lead type
}

/**
 * Convierte un array de leads de la API al formato del frontend
 * @param apiLeads - Array de leads en formato backend
 * @returns Array de leads en formato frontend
 */
export function adaptAPILeadsToFrontend(apiLeads: APILead[]): Lead[] {
  if (apiLeads.length > 0) {
  }
  return apiLeads.map(adaptAPILeadToFrontend);
}

/**
 * Convierte un lead del frontend (radar format) al formato de la API (para crear/actualizar)
 * @param lead - Lead en formato frontend (radar legacy format)
 * @returns Lead en formato API (snake_case)
 */
export function adaptFrontendLeadToAPI(lead: Partial<Lead>): Partial<APILead> {
  // Extract name parts from radar format
  const nameParts = lead.name?.split(' ') || [];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    // Solo incluir campos si están presentes
    ...(lead.id && { id: lead.id }),
    ...(firstName && { first_name: firstName }),
    ...(lastName && { last_name: lastName }),
    ...(lead.email && { email: lead.email }),
    ...(lead.phone && { phone: lead.phone }),
    ...(lead.company && { company_name: lead.company }),
    ...(lead.status && { status: lead.status as any }), // Pass through as string
    ...(lead.perfil?.profesion && { position: lead.perfil.profesion }),
    ...(lead.scoring?.total !== undefined && { lead_score: lead.scoring.total }),
    ...(lead.value !== undefined && { total_quote_value: lead.value }),
    ...(lead.perfil?.ingresos && {
      budget_max: lead.perfil.ingresos,
    }),
    ...(lead.unidadInteres && { interested_units: [{ id: lead.unidadInteres }] as any }),
    ...(lead.lastContact && { last_contact_date: lead.lastContact }),
  };
}
